import { Box, Typography, Container, Card, CardContent, CardActionArea, Grid, Button, CircularProgress } from '@mui/material';
import { Mail, EventNote, PhotoLibrary, PhotoCamera } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { needsPartyPerson, personalGuestIcon, personalGuestName, type GuestIdentity, type SitePhotoMap } from '@wedding/shared';
import EntityIconMark from '../../../shared/src/components/icons/EntityIconMark';
import { buildServiceHref } from '../../../shared/src/utils/routing';
import CountdownSection from './CountdownSection';

interface HomePageProps {
  guest: GuestIdentity | null;
  guestChecked: boolean;
  visitorMode: boolean;
  weddingDate: string | null;
  timezone?: string;
  sitePhotos: SitePhotoMap;
}

const NAV_CARDS = [
  {
    titleKey: 'home.invitation',
    descKey: 'home.invitationDesc',
    icon: <Mail sx={{ fontSize: 48 }} />,
    href: buildServiceHref('invitation'),
    color: '#8B6F5E',
    photoSlot: 'home_card_invitation',
  },
  {
    titleKey: 'home.programme',
    descKey: 'home.programmeDesc',
    icon: <EventNote sx={{ fontSize: 48 }} />,
    href: buildServiceHref('programme'),
    color: '#C9A87C',
    photoSlot: 'home_card_programme',
  },
  {
    titleKey: 'home.gallery',
    descKey: 'home.galleryDesc',
    icon: <PhotoLibrary sx={{ fontSize: 48 }} />,
    href: buildServiceHref('gallery'),
    color: '#7B9E6B',
    photoSlot: 'home_card_gallery',
  },
  {
    titleKey: 'home.upload',
    descKey: 'home.uploadDesc',
    icon: <PhotoCamera sx={{ fontSize: 48 }} />,
    href: buildServiceHref('upload'),
    color: '#6B8A9E',
    photoSlot: 'home_card_upload',
  },
];

export default function HomePage({
  guest,
  guestChecked,
  visitorMode,
  weddingDate,
  timezone,
  sitePhotos,
}: HomePageProps) {
  const { t, i18n } = useTranslation();

  const bgStyle = sitePhotos['home_bg'] ? {
    backgroundImage: `url(${sitePhotos['home_bg']})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    minHeight: { xs: '100%', sm: '100vh' },
  } : {};

  return (
    <Box sx={bgStyle}>
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 6, md: 8 } }}>
        {sitePhotos['home_hero'] && (
          <Box
            component="img"
            src={sitePhotos['home_hero']}
            alt="Hero"
            sx={{
              width: '100%',
              maxHeight: 400,
              objectFit: 'cover',
              borderRadius: 3,
              mb: { xs: 2, sm: 4 },
            }}
          />
        )}

        <Box sx={{ mb: { xs: 0, sm: 6 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <Typography variant="h2" gutterBottom>
            {t('home.welcome')}
          </Typography>
          {!guestChecked ? (
            <Box display="flex" justifyContent="center" sx={{ mt: 1 }}>
              <CircularProgress size={20} />
            </Box>
          ) : !guest || needsPartyPerson(guest) ? (
            <>
              <Typography variant="h6" color="text.secondary">
                {visitorMode && !guest ? t('visitor.browsing') : t('home.tagline')}
              </Typography>
              <Button href="/identify" variant="outlined" sx={{ mt: 2 }}>
                {t('home.identify')}
              </Button>
            </>
          ) : (
            <Typography variant="h6" color="text.secondary" component="div" sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 1 }}>
              <EntityIconMark icon={personalGuestIcon(guest)} />
              <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
                {t('home.hello', { name: personalGuestName(guest, i18n.language) })}
              </Box>
            </Typography>
          )}
        </Box>

        {weddingDate && <CountdownSection weddingDate={weddingDate} timezone={timezone} />}

        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Grid container spacing={{ xs: 1.5, sm: 3 }}>
            {NAV_CARDS.map((card) => {
              const cardBg = sitePhotos[card.photoSlot];
              return (
                <Grid key={card.titleKey} size={{ xs: 6 }}>
                  <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
                    {cardBg && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          backgroundImage: `url(${cardBg})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          opacity: 0.2,
                        }}
                      />
                    )}
                    <CardActionArea
                      href={card.href}
                      sx={{ height: '100%', p: 2, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 0, '&:last-child': { pb: 0 } }}>
                        <Box sx={{ color: card.color, mb: 2 }}>{card.icon}</Box>
                        <Typography variant="h5" gutterBottom textAlign="center">{t(card.titleKey)}</Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                          {t(card.descKey)}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
